/* ───────────────────── NotificationBell.tsx ─────────────────────
   Campana con recuento en tiempo real usando Socket.IO (optimizada)
   ─────────────────────────────────────────────────────────────── */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Button,
  Box,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useTranslation } from "react-i18next";
import api from "../api/axios";
import { NOTIF_LABELS } from "../constants/enums";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import { useBroadcastModal } from "../components/BroadcastModalProvider";

/* ═════════ tipos ═════════ */
interface Notif {
  id: number;
  event: string;
  payload: Record<string, any>; // puede contener reservas[] o fecha
  createdAt: string;
  priority?: "P0" | "P1" | "P2";
  requiresAction?: boolean;
}

interface DigestRow {
  id: number;
  fecha: string; // YYYY-MM-DD
  hora_inicio: string; // HH:mm:ss
  profesor: string;
  alumno: string;
}

/* Cooldowns para evitar ráfagas */
const MIN_DATA_INTERVAL_MS = 15_000; // 15s entre lecturas de lista
const MIN_BADGE_INTERVAL_MS = 10_000; // 10s entre lecturas de badge
const BADGE_DEBOUNCE_MS = 1_500; // coalescar eventos socket → badge
const POLL_INTERVAL_MS = 300_000; // 5 min

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation<"translation">();
  const { enqueueBroadcast } = useBroadcastModal();

  const [items, setItems] = useState<Notif[]>([]);
  const [loading, setLoading] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [badge, setBadge] = useState<number>(0);

  // digest
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestRows, setDigestRows] = useState<DigestRow[]>([]);

  // refs para control fino
  const inflightDataRef = useRef(false);
  const inflightBadgeRef = useRef(false);
  const lastDataTsRef = useRef(0);
  const lastBadgeTsRef = useRef(0);
  const badgeDebounceRef = useRef<number | null>(null);
  const pollRef = useRef<number | null>(null);
  const { socket, connected } = useSocket();

  const startPoll = () => {
    if (pollRef.current) return;
    pollRef.current = window.setInterval(() => {
      fetchData();
      fetchBadge();
    }, POLL_INTERVAL_MS);
  };
  const stopPoll = () => {
    if (!pollRef.current) return;
    clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const scheduleBadgeRefresh = () => {
    if (badgeDebounceRef.current) return;
    badgeDebounceRef.current = window.setTimeout(() => {
      fetchBadge();
      if (badgeDebounceRef.current) {
        clearTimeout(badgeDebounceRef.current);
        badgeDebounceRef.current = null;
      }
    }, BADGE_DEBOUNCE_MS);
  };

  /* ═════════ helpers fetch (con cooldown + background) ═════════ */
  const fetchData = async (opts?: { force?: boolean }) => {
    if (!user) return;
    const now = Date.now();
    if (!opts?.force && now - lastDataTsRef.current < MIN_DATA_INTERVAL_MS) return;
    if (inflightDataRef.current) return;

    inflightDataRef.current = true;
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", {
        params: { unread: true, take: 10 },
        // evita toasts de rate limit si tu interceptor lo soporta
        _background: true,
      } as any);
      const lista = Array.isArray(data) ? data : ((data as any).rows ?? []);
      const visibles = (lista as Notif[]).filter((n) => n.event !== "BROADCAST");
      setItems(visibles);
    } catch (e) {
      // Error leyendo notifs
    } finally {
      lastDataTsRef.current = Date.now();
      inflightDataRef.current = false;
      setLoading(false);
    }
  };

  const fetchBadge = async (opts?: { force?: boolean }) => {
    if (!user) return;
    const now = Date.now();
    if (!opts?.force && now - lastBadgeTsRef.current < MIN_BADGE_INTERVAL_MS) return;
    if (inflightBadgeRef.current) return;

    inflightBadgeRef.current = true;
    try {
      const { data } = await api.get<number>("/notifications/badge-count", {
        _background: true,
      } as any);
      const n = typeof data === "number" ? Math.max(0, Math.min(99, data)) : 0;
      setBadge(n);
    } catch {
      // fallback suave si el endpoint aún no existe
      setBadge((prev) => (prev > 0 ? prev : items.length));
    } finally {
      lastBadgeTsRef.current = Date.now();
      inflightBadgeRef.current = false;
    }
  };

  const openDocsPage = () => {
    if (user?.roles?.includes("profesor")) navigate("/profesor/documentos");
    else navigate("/notificaciones");
  };

  /* ═════════ Socket.IO (compartido) + polling de respaldo ═════════ */
  useEffect(() => {
    if (!user) return;

    // fetch inicial
    fetchData({ force: true });
    fetchBadge({ force: true });

    // polling como backup si no hay socket conectado
    if (!connected) startPoll();
    else stopPoll();

    // refrescar al volver a la pestaña
    const onVis = () => {
      if (!document.hidden) {
        fetchData({ force: true });
        fetchBadge({ force: true });
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      stopPoll();
      if (badgeDebounceRef.current) {
        clearTimeout(badgeDebounceRef.current);
        badgeDebounceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, connected]);

  // Escuchar notificaciones del socket compartido
  useEffect(() => {
    if (!socket) return;

    const handler = (n: Notif) => {
      if (n.event === "BROADCAST") {
        enqueueBroadcast(n);
      } else {
        setItems((prev) => [n, ...prev]);
      }
      scheduleBadgeRefresh();
    };

    socket.on("new-notification", handler);
    return () => {
      socket.off("new-notification", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  /* ═════════ helpers UI ═════════ */
  const openDestino = (reservaId?: number) => {
    if (!reservaId) return;

    const isProf = user?.roles?.includes("profesor");
    const isCli = user?.roles?.includes("cliente");
    const path = isProf ? "/profesor/agenda" : isCli ? "/cliente/agenda" : "/agenda";
    navigate(path, { state: { reservaId } });
  };

  const handleDigestClick = (reservas: DigestRow[]) => {
    setDigestRows(reservas);
    setDigestOpen(true);
  };

  const handleClick = async (n: Notif) => {
    try {
      await api.patch(`/notifications/${n.id}/read`, null, {
        _background: true,
      } as any);
    } catch {
      // No se pudo marcar como leída
    } finally {
      setItems((prev) => prev.filter((x) => x.id !== n.id));
      setAnchor(null);
      // ajuste optimista + refresh coalescado
      setBadge((b) => Math.max(0, b - 1));
      scheduleBadgeRefresh();
    }

    if (n.event === "PENDING_REBOOK_WEEKLY_DIGEST") {
      handleDigestClick(n.payload?.reservas ?? []);
      return;
    }

    if (n.event === "DOC_AVAILABLE") {
      openDocsPage();
      return;
    }

    if (n.event === "DOC_PENDING_SIGNATURE") {
      if (n.payload?.signUrl) {
        window.open(n.payload.signUrl, "_blank", "noopener,noreferrer");
      } else {
        openDocsPage();
      }
      return;
    }

    if (["VAC_REQ_CREATED", "VAC_REQ_BATCH_CREATED", "ABS_REQ_CREATED"].includes(n.event)) {
      if (user?.roles?.includes("hr")) {
        navigate("/admin/manager/profesor-vacaciones", {
          state: { profId: n.payload.profId, batchId: n.payload.batchId },
        });
        return;
      }
    }

    if (n.event === "BROADCAST") return;

    const href = n.payload?.href ?? n.payload?.formURL ?? n.payload?.url ?? null;
    if (href) {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }

    openDestino(n.payload?.reservaId);
  };

  if (!user) return null;

  const badgeDisplay: string | number = badge > 9 ? "9+" : badge;

  /* ═════════ render ═════════ */
  return (
    <>
      <IconButton
        onClick={(e) => {
          setAnchor(e.currentTarget);
          // Refresh suave al abrir: respetará cooldowns
          fetchData();
          fetchBadge();
        }}
        aria-label={
          loading
            ? (t("notificationBell.loading") as string)
            : (t("notificationBell.open") as string)
        }
      >
        {loading ? (
          <CircularProgress size={20} />
        ) : (
          <Badge
            badgeContent={badgeDisplay}
            overlap="circular"
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: "#1e9e85", // color personalizado
                color: "#fff", // color del número
              },
            }}
          >
            <NotificationsIcon />
          </Badge>
        )}
      </IconButton>

      {/* ===== menú desplegable ===== */}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        MenuListProps={{ dense: true }}
      >
        {items.length === 0 && <MenuItem disabled>{t("notificationBell.empty")}</MenuItem>}

        {items.map((n) => {
          const rawFecha = n.payload?.fecha as string | undefined;
          const fechaFmt =
            typeof rawFecha === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(rawFecha)
              ? rawFecha
              : rawFecha
                ? dayjs(rawFecha).format("DD/MM/YYYY")
                : undefined;

          const primary =
            n.event === "BROADCAST"
              ? (n.payload?.title as string) ||
                (t("notifications.broadcast.title", "Aviso") as string)
              : String(
                  t(NOTIF_LABELS[n.event] ?? n.event, {
                    ...n.payload,
                    fecha: fechaFmt,
                  })
                );

          return (
            <MenuItem key={n.id} onClick={() => handleClick(n)} sx={{ maxWidth: 320 }}>
              <ListItemText
                primary={primary}
                secondary={
                  n.event === "BROADCAST" && n.payload?.body
                    ? String(n.payload.body).slice(0, 120)
                    : dayjs(n.createdAt).format("D/M/YYYY • HH:mm")
                }
              />
            </MenuItem>
          );
        })}

        {items.length > 0 && <Divider />}

        <MenuItem
          onClick={() => {
            setAnchor(null);
            navigate("/notificaciones");
          }}
        >
          {t("notificationBell.viewAll")}
        </MenuItem>
      </Menu>

      {/* ===== diálogo digest semanal ===== */}
      <Dialog open={digestOpen} onClose={() => setDigestOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {t("pending.digest.title", "Clases pendientes de reagendar esta semana")}
        </DialogTitle>

        <DialogContent>
          {digestRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("pending.digest.empty", "No hay clases pendientes para esta semana.")}
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>{t("common.date", "Fecha")}</TableCell>
                    <TableCell>{t("common.time", "Hora")}</TableCell>
                    <TableCell>{t("common.teacher", "Profesor")}</TableCell>
                    <TableCell>{t("common.student", "Alumno")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {digestRows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{dayjs(r.fecha).format("DD/MM/YYYY")}</TableCell>
                      <TableCell>{r.hora_inicio.slice(0, 5)}</TableCell>
                      <TableCell>{r.profesor}</TableCell>
                      <TableCell>{r.alumno}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          <Box sx={{ textAlign: "right", mt: 2 }}>
            <Button onClick={() => setDigestOpen(false)}>{t("common.close", "Cerrar")}</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
